import React from 'react';
import { motion } from 'framer-motion';
import { Waves, CheckCircle, ArrowRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const DesalinationIndustryPage: React.FC = () => {
  const systems = [
    {
      title: 'Inline Pressure Filters',
      description: 'RO/UF pretreatment and membrane protection',
      link: '/filtration/inline/big'
    },
    {
      title: 'Self-Cleaning Suction Screens',
      description: 'Raw seawater and brackish intake protection',
      link: '/filtration/suction-screens/self-cleaning'
    },
    {
      title: 'Standard Suction Screens',
      description: 'Heavy-duty intake screens for high-flow applications',
      link: '/filtration/suction-screens/standard'
    }
  ];

  const benefits = [
    'Inline stainless-steel filtration before reverse osmosis, UF, or NF membranes',
    'Self-cleaning suction screens for raw seawater or brackish intake protection',
    'Designed for continuous operation and easy integration with existing systems',
    'Protect high-pressure pumps and extend membrane service life',
    'Automated cleaning options to minimize maintenance and downtime',
    'Custom mesh sizes and flow configurations for specific water quality'
  ];

  const applications = [
    {
      title: 'RO/UF Pretreatment',
      description: 'Protect reverse osmosis and ultrafiltration membranes from debris and suspended solids, reducing fouling and extending membrane life.'
    },
    {
      title: 'Seawater Intake',
      description: 'Self-cleaning intake screens for coastal desalination facilities, handling high flows and variable marine debris loads.'
    },
    {
      title: 'Brackish Water Reuse',
      description: 'Inline filtration for inland brackish-water systems, removing sediment and organics before membrane treatment.'
    },
    {
      title: 'Municipal Reclamation Facilities',
      description: 'Pre-filtration for water-reuse applications, protecting advanced treatment systems and ensuring consistent water quality.'
    }
  ];

  return (
    <>
      <SEOHead
        title="Desalination & Water Reuse Filtration Systems | CTC Waterworks"
        description="Pre-filtration and intake protection for RO, UF, and water reclamation systems. Built for continuous operation."
        canonicalPath="/industries/desalination"
      />
      <div className="min-h-screen bg-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-cyan-600 to-blue-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                    <Waves className="text-white" size={32} />
                  </div>
                  <h1 className="text-5xl font-bold">
                    Desalination & Water Reuse
                  </h1>
                </div>
                <p className="text-2xl text-cyan-100 mb-6 font-light leading-relaxed">
                  In modern desalination and reuse facilities, pre-filtration is critical to protecting membranes and high-pressure pumps. CTC filtration systems deliver reliable, automated solids removal with minimal maintenance.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-cyan-100">Inline stainless-steel filtration before reverse osmosis, UF, or NF membranes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-cyan-100">Self-cleaning suction screens for raw seawater or brackish intake protection</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-cyan-100">Designed for continuous operation and easy integration with existing systems</span>
                  </li>
                </ul>
              </motion.div>

              <div className="bg-white rounded-lg shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Get Expert Help</h3>
                <Link
                  to="/contact?topic=desalination-filtration"
                  className="block w-full bg-cyan-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors text-center shadow-lg mb-4"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Expert
                </Link>
                <p className="text-sm text-gray-600 text-center">
                  Get design assistance, submittals, and spec packs for your next project.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Applications Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Desalination & Water Reuse Applications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {applications.map((app, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-cyan-50 rounded-lg p-6 border border-cyan-200 hover:border-cyan-600 hover:shadow-lg transition-all"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{app.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{app.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Systems Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Recommended Filtration Systems
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {systems.map((system, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={system.link}
                    className="block bg-white rounded-lg shadow-lg p-6 h-full hover:shadow-xl transition-all border-2 border-transparent hover:border-cyan-600 group"
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-cyan-600">
                      {system.title}
                    </h3>
                    <p className="text-gray-700 mb-4 leading-relaxed">{system.description}</p>
                    <span className="inline-flex items-center text-cyan-600 font-semibold">
                      Learn More <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Why CTC for Desalination Facilities
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start"
                >
                  <CheckCircle className="text-cyan-600 mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700 text-lg">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-cyan-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Let's Engineer Your Solution</h2>
            <p className="text-xl text-cyan-100 mb-8">
              From RO pretreatment to seawater intake protection, our systems are built for continuous operation in demanding desalination environments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact?topic=desalination-filtration"
                className="inline-flex items-center justify-center bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 transition-colors shadow-lg"
              >
                <span>Talk to a Filtration Expert</span>
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <a
                href="tel:+15099017476"
                className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-cyan-600 transition-colors"
              >
                <span>(509) 901-7476</span>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default DesalinationIndustryPage;
